/* eslint-disable no-undef */
import { ref, shallowRef, watch, provide } from "vue"
import * as pdfjsLib from "pdfjs-dist"
import PdfjsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?worker"
import useScopeVars from "../../../shared/useScopeVars.js"
import { addScopeVars } from "@/items_preview/shared/api/utilities"

// Set the worker source path
pdfjsLib.GlobalWorkerOptions.workerPort = new PdfjsWorker()

import { setupKeyboard } from "../common/setupKeyboard"
import { setupContainer } from "../common/setupContainer"
import { setupScaling } from "../common/setupScaling"
import { setupPanning } from "../common/setupPanning"
import { setupWordTooltip } from "../common/setupWordTooltip"
import { setupLoading } from "../common/setupLoading"

const setupViewerState = props => {
  const inputFileId = ref(undefined)
  const pdfUrl = ref(undefined)
  const currentPage = ref(undefined)
  const highlightBlockIds = shallowRef(undefined)
  const highlightBlockScores = shallowRef(undefined)
  const isShowExtractedWords = ref(undefined)
  const isForceShowHighlightBlocks = ref(undefined)
  const initialCenterRatio = ref(undefined)
  const initialScale = ref(undefined)
  const detailPageUrl = ref(undefined)
  const attributeSources = shallowRef(undefined)
  const seeSourceAttributeSlug = ref(undefined)
  const isOnlyShowAttributeSourcePages = ref(false)
  const isPositionalTextFetchable = ref(true)

  /** isOnlyShowAttributeSourcePages toggle function */
  const toggleIsOnlyShowAttributeSourcePages = () => {
    isOnlyShowAttributeSourcePages.value = !isOnlyShowAttributeSourcePages.value
  }

  /** We watch for changes in the props and update the values of the state. */
  watch(
    props,
    props => {
      if (
        typeof props.inputFileId === "undefined" ||
        typeof props.pdfUrl === "undefined" ||
        typeof props.currentPage === "undefined" ||
        typeof props.highlightBlockIds === "undefined" ||
        typeof props.highlightBlockScores === "undefined" ||
        typeof props.isShowExtractedWords === "undefined" ||
        typeof props.isForceShowHighlightBlocks === "undefined"
      ) {
        return
      }

      inputFileId.value = props.inputFileId
      pdfUrl.value = props.pdfUrl
      currentPage.value = props.currentPage
      highlightBlockIds.value = props.highlightBlockIds
      highlightBlockScores.value = props.highlightBlockScores
      isShowExtractedWords.value = props.isShowExtractedWords
      isForceShowHighlightBlocks.value = props.isForceShowHighlightBlocks
      initialCenterRatio.value = props.initialCenterRatio
      initialScale.value = props.initialScale
      detailPageUrl.value = props.detailPageUrl
      attributeSources.value = props.attributeSources
      seeSourceAttributeSlug.value = props.seeSourceAttributeSlug
      isOnlyShowAttributeSourcePages.value = !!props.seeSourceAttributeSlug

      if (props.isPositionalTextFetchable !== undefined) {
        isPositionalTextFetchable.value = props.isPositionalTextFetchable
      }

      /*
       * When we are in SeeSource mode, we don't want to set the page from the props
       * as we should be setting the first page coming out of the AttributeSources instead
       */
      if (!props.seeSourceAttributeSlug) currentPage.value = props.currentPage
    },
    { immediate: true }
  )

  return {
    inputFileId,
    pdfUrl,
    currentPage,
    highlightBlockIds,
    highlightBlockScores,
    isShowExtractedWords,
    isForceShowHighlightBlocks,
    initialCenterRatio,
    initialScale,
    detailPageUrl,
    attributeSources,
    seeSourceAttributeSlug,
    isOnlyShowAttributeSourcePages,
    isPositionalTextFetchable,
    toggleIsOnlyShowAttributeSourcePages
  }
}

const setupPaging = ({ viewerState, pdfDocument, containerState }) => {
  const currentPage = ref(undefined)
  const totalPage = ref(undefined)
  const availablePages = shallowRef([])

  /**  Go to the previous page. */
  const previousPage = () => {
    if (!currentPage.value) return

    const currentIndex = availablePages.value.indexOf(currentPage.value)
    if (currentIndex === -1) return

    const newIndex = currentIndex - 1
    if (newIndex < 0) return

    currentPage.value = availablePages.value[newIndex]

    // Scroll to the page if it is not in the view and there are no special "modes" on
    if (
      !viewerState.isForceShowHighlightBlocks.value &&
      !viewerState.isShowExtractedWords.value &&
      !viewerState.seeSourceAttributeSlug?.value &&
      containerState.element.value
    ) {
      const pageElement = containerState.element.value.querySelector(`[data-page-number="${currentPage.value}"]`)
      pageElement.scrollIntoView({
        behavior: "instant"
      })
    }
  }

  /** Go to the next page. */
  const nextPage = () => {
    if (!currentPage.value) return

    const currentIndex = availablePages.value.indexOf(currentPage.value)
    if (currentIndex === -1) return

    const newIndex = currentIndex + 1
    if (newIndex > availablePages.value.length - 1) return

    currentPage.value = availablePages.value[newIndex]

    // Scroll to the page if it is not in the view and there are no special "modes" on
    if (
      !viewerState.isForceShowHighlightBlocks.value &&
      !viewerState.isShowExtractedWords.value &&
      !viewerState.seeSourceAttributeSlug?.value &&
      containerState.element.value
    ) {
      const pageElement = containerState.element.value.querySelector(`[data-page-number="${currentPage.value}"]`)
      pageElement.scrollIntoView({
        behavior: "instant"
      })
    }
  }

  /** Go to a specific page. */
  const gotoPage = page => {
    const index = availablePages.value.indexOf(page)
    if (index === -1) return

    currentPage.value = page
  }

  // keep currentPage in sync with viewerState.currentPage
  watch(
    viewerState.currentPage,
    viewerCurrentPage => {
      currentPage.value = viewerCurrentPage
    },
    { immediate: true }
  )

  // when the pdfDocument changes, let's update also the total of its pages
  watch(
    pdfDocument,
    pdfDocument => {
      totalPage.value = undefined

      if (!pdfDocument) return

      totalPage.value = pdfDocument.numPages
    },
    { immediate: true }
  )

  /*
   * When the input file id changes, or the attribute sources change, we need to update the available pages.
   * If the seeSourceAttributeSlug is not set, we show all pages.
   * If the seeSourceAttributeSlug is set, we show only the pages that have attribute sources.
   */
  watch(
    [
      viewerState.inputFileId,
      viewerState.attributeSources,
      viewerState.seeSourceAttributeSlug,
      viewerState.isOnlyShowAttributeSourcePages,
      totalPage
    ],
    (
      [
        viewerInputFileId,
        viewerAttributeSources,
        viewerSeeSourceAttributeSlug,
        viewerIsOnlyShowAttributeSourcePages,
        totalPage
      ],
      [
        _oldViewerInputFileId,
        _oldViewerAttributeSources,
        _oldViewerSeeSourceAttributeSlug,
        _oldViewerIsOnlyShowAttributeSourcePages,
        _oldTotalPage
      ]
    ) => {
      if (!totalPage) return

      /*
       * If we are setting for the first time (from value false) a seeSourceAttributeSlug, we set the isOnlyShowAttributeSourcePages to true
       * Then we return, because the watcher will be called again with the new values
       */
      if (viewerSeeSourceAttributeSlug && !_oldViewerSeeSourceAttributeSlug) {
        viewerState.isOnlyShowAttributeSourcePages.value = true
        return
      }

      /*
       * We can have two possible returned values:
       * - Normal mode: we return all pages in ascending order
       * - See Source Attribute pages only: we return the pages that have sources in descending order
       *
       * Normal mode:
       * - no seeSourceAttributeSlug
       * - seeSourceAttributeSlug but isOnlyShowAttributeSourcePages is false
       * - no attributeSources
       *
       * See Source Attribute pages mode:
       * - seeSourceAttributeSlug && isOnlyShowAttributeSourcePages is true && attributeSources
       */
      // only two cases here, sort or non sort

      // --- Normal mode: We return all pages in ascending order
      if (
        !viewerState.seeSourceAttributeSlug.value ||
        !viewerIsOnlyShowAttributeSourcePages ||
        !viewerAttributeSources?.length
      ) {
        availablePages.value = Array.from(Array(totalPage).keys()).map(page => page + 1)

        /*
         * there could be cases where, starting from a SeeSource Mode, we don't have any attribute source
         * in which case, we are fallbacking on normal mode, but the current page might not be set yet
         * (as normally in seeSource it depends on the attributeSources fetched, here not available)
         */
        if (typeof viewerState.currentPage.value === "undefined") currentPage.value = viewerState.currentPage.value || 1

        /*
         * If we are returning to Normal mode from See Source Attribute mode,
         * and if highlights were available, we set the currentPage to the original page
         */
        if (
          _oldViewerSeeSourceAttributeSlug &&
          !viewerSeeSourceAttributeSlug &&
          viewerState.highlightBlockIds.value?.length > 0 &&
          viewerState.isForceShowHighlightBlocks.value
        ) {
          currentPage.value = viewerState.currentPage.value
        }
        return
      }

      // --- See Source Attribute pages mode
      // we start the re-ordering of the sources based off the score
      const sortedSources = viewerAttributeSources
        .filter(source => !source.file_id || source.file_id === viewerInputFileId)
        .toSorted((a, b) => b.ocr_block_score - a.ocr_block_score)

      // We create a set of pages that have sources
      let pageSet = new Set()
      for (const source of sortedSources) {
        pageSet.add(source.page)
      }

      // We create a set of all pages
      const newPages = Array.from(pageSet)

      // We update the available pages and set the current page to the first page
      // The pages here will include only the "source pages" and will be in descending order based off the score
      availablePages.value = newPages
      if (newPages.length > 0) currentPage.value = newPages[0]
      else {
        currentPage.value = viewerState.currentPage.value
      }
    },
    { immediate: true }
  )

  return {
    currentPage,
    totalPage,
    availablePages,
    previousPage,
    nextPage,
    gotoPage
  }
}

const setupCurrentPage = ({ viewerState, pdfDocument, pagingState }) => {
  // Data
  const imageUrl = ref(undefined)
  const positionalText = shallowRef(undefined)
  const highlightBlockIds = shallowRef(undefined)
  const highlightBlockScores = shallowRef(undefined)
  const attributeSources = shallowRef(undefined)
  const width = ref(undefined)
  const height = ref(undefined)
  const containerCenterRatio = ref(undefined)
  const { dataSpaceSlug } = useScopeVars()

  // Methods
  const setHighlightBlocks = () => {
    if (
      positionalText.value[0] &&
      positionalText.value[0].content?.some(({ id }) => viewerState.highlightBlockIds.value.includes(id))
    ) {
      highlightBlockIds.value = viewerState.highlightBlockIds.value
      highlightBlockScores.value = viewerState.highlightBlockScores.value
    } else {
      highlightBlockIds.value = []
      highlightBlockScores.value = []
    }
  }

  // Watchers
  /* When the pdfDocument, viewerInputFileId, or pagingCurrentPage changes, we update the current page. */
  watch(
    [pdfDocument, viewerState.inputFileId, pagingState.currentPage],
    async ([pdfDocument, viewerInputFileId, pagingCurrentPage]) => {
      imageUrl.value = undefined
      positionalText.value = undefined
      highlightBlockIds.value = undefined
      highlightBlockScores.value = undefined
      width.value = undefined
      height.value = undefined

      if (!viewerInputFileId || !pdfDocument || !pagingCurrentPage) return

      const page = await pdfDocument.getPage(pagingCurrentPage)

      const scaleFactor = 2

      // Generate image url
      const viewport = page.getViewport({ scale: scaleFactor })
      const canvas = document.createElement("canvas")
      const context = canvas.getContext("2d")
      canvas.width = viewport.width
      canvas.height = viewport.height
      await page.render({ canvasContext: context, viewport }).promise
      imageUrl.value = canvas.toDataURL()

      let url = `/api/ui/input_files/${viewerInputFileId}?position_page_number=${pagingCurrentPage}`
      url = addScopeVars(url, "", dataSpaceSlug)

      try {
        if (viewerState.isPositionalTextFetchable.value) {
          // Get positional text via API
          const positionalTextResponse = await fetch(url)
          const { positional_text } = await positionalTextResponse.json()

          // We compose the positional text with the viewport and the table cells if available
          positionalText.value = positional_text
            .filter(({ page_number }) => page_number === pagingCurrentPage)
            .map(item => {
              const inchWidth = item.width
              const inchHeight = item.height

              return {
                ...item,
                width: viewport.width,
                height: viewport.height,
                content: item.content.map(boundingBox => {
                  return {
                    ...boundingBox,
                    content: [
                      (boundingBox.content[0] / inchWidth) * viewport.width,
                      (boundingBox.content[1] / inchHeight) * viewport.height,
                      (boundingBox.content[2] / inchWidth) * viewport.width,
                      (boundingBox.content[3] / inchHeight) * viewport.height,
                      boundingBox.content[4]
                    ]
                  }
                }),
                tables:
                  item.tables &&
                  item.tables.map(table => {
                    return {
                      ...table,
                      cells: table.cells.map(cell => {
                        return {
                          ...cell,
                          boundingRegions: cell.boundingRegions.map(boundingRegion => {
                            return {
                              ...boundingRegion,
                              polygon: boundingRegion.polygon.map((value, index) => {
                                if (index % 2 === 0) {
                                  return (value / inchWidth) * viewport.width
                                } else {
                                  return (value / inchHeight) * viewport.height
                                }
                              })
                            }
                          })
                        }
                      }),
                      boundingRegions: table.boundingRegions.map(boundingRegion => {
                        return {
                          ...boundingRegion,
                          polygon: boundingRegion.polygon.map((value, index) => {
                            if (index % 2 === 0) {
                              return (value / inchWidth) * viewport.width
                            } else {
                              return (value / inchHeight) * viewport.height
                            }
                          })
                        }
                      })
                    }
                  })
              }
            })
        } else {
          positionalText.value = []
        }
      } catch (error) {
        positionalText.value = []
      }

      // If in the positional text there are valid highlight blocks, we set them as the current highlight blocks
      setHighlightBlocks()
    },
    { immediate: true }
  )

  // When the initialCenterRatio changes, we update the containerCenterRatio
  watch(
    viewerState.initialCenterRatio,
    initialCenterRatio => {
      if (!initialCenterRatio) return

      containerCenterRatio.value = {
        pageNumber: viewerState.currentPage,
        top: initialCenterRatio.top,
        left: initialCenterRatio.left
      }
    },
    { immediate: true }
  )

  // When the attribute sources change, we update the attribute sources for the current page
  watch(
    [viewerState.inputFileId, viewerState.attributeSources, pagingState.currentPage],
    ([viewerInputFileId, viewerAttributeSources, pagingCurrentPage]) => {
      if (!viewerInputFileId || !pagingCurrentPage) {
        attributeSources.value = undefined
        return
      }

      if (!viewerAttributeSources) {
        attributeSources.value = []
        return
      }

      attributeSources.value = viewerAttributeSources
        .filter(({ file_id }) => file_id === viewerInputFileId)
        .filter(({ page }) => page === pagingCurrentPage)
    },
    { immediate: true }
  )

  return {
    imageUrl,
    positionalText,
    highlightBlockIds,
    highlightBlockScores,
    attributeSources,
    width,
    height,
    containerCenterRatio
  }
}

const setupControl = ({ viewerState, pagingState, scalingState }) => {
  const isThumbnailsVisible = ref(false)

  /** Toggle the visibility of the thumbnails. */
  const toggleThumbnails = () => {
    isThumbnailsVisible.value = !isThumbnailsVisible.value
  }

  // When the attributeSources change, we show the thumbnails
  watch(
    [viewerState.inputFileId, viewerState.attributeSources],
    ([viewerInputFileId, viewerAttributeSources], [prevViewerInputFileId, prevViewerAttributeSources]) => {
      if (!viewerInputFileId || !viewerAttributeSources?.length) return

      // when the input file changes or the attribute sources change, we force the thumbnails to be visible
      const isInputFileDifferent = prevViewerInputFileId !== viewerInputFileId
      const getSourceIds = sources =>
        sources?.map(({ file_id, ocr_block_id }) => `file-id-${file_id}_ocr-block-id-${ocr_block_id}`).join("+")
      const isAttributeSourcesDifferent =
        getSourceIds(prevViewerAttributeSources) !== getSourceIds(viewerAttributeSources)

      if (isInputFileDifferent || isAttributeSourcesDifferent) {
        viewerState.isOnlyShowAttributeSourcePages.value = true
        isThumbnailsVisible.value = true
      }
    },
    { immediate: true }
  )

  return {
    ...pagingState,
    ...scalingState,
    isThumbnailsVisible,
    toggleThumbnails
  }
}

const setupThumbnails = ({ viewerState, pdfDocument, containerState, pagingState }) => {
  const thumbnails = ref([])

  /** Go to a specific page. */
  const gotoPage = event => {
    const pageNumber = parseInt(event.currentTarget.dataset.pageNumber, 10)
    if (isNaN(pageNumber)) return

    pagingState.gotoPage(pageNumber)

    // Scroll to the page if it is not in the view and there are no special "modes" on
    if (
      !viewerState.isShowExtractedWords.value &&
      !viewerState.isForceShowHighlightBlocks.value &&
      !viewerState.seeSourceAttributeSlug &&
      containerState.element.value
    ) {
      const pageElement = containerState.element.value.querySelector(`[data-page-number="${pageNumber}"]`)
      pageElement.scrollIntoView({
        behavior: "instant"
      })
    }
  }

  /* When the pdfDocument changes, we generate the thumbnails. */
  watch(
    [pagingState.availablePages],
    async ([pagingAvailablePages]) => {
      if (!pdfDocument.value) return
      const newThumbnails = []

      for (const pageNumber of pagingAvailablePages) {
        // We generate te thumbnail for each page
        const page = await pdfDocument.value.getPage(pageNumber)
        const viewport = page.getViewport({ scale: 1 })
        const canvas = document.createElement("canvas")
        const context = canvas.getContext("2d")
        canvas.width = viewport.width
        canvas.height = viewport.height
        await page.render({ canvasContext: context, viewport }).promise
        const url = canvas.toDataURL()

        // We filter the sources (if any) for the current page
        const attributeSources = viewerState.attributeSources.value || []
        const sources = attributeSources
          .filter(source => !source.file_id || source.file_id === viewerState.inputFileId.value)
          .filter(source => source.page === pageNumber)

        // We calculate the score of the page, by looking for the highest score in the sources
        const score = sources.reduce((acc, current) => {
          if (current.ocr_block_score > acc) {
            return current.ocr_block_score
          }
          return acc
        }, 0)

        // We add the thumbnail to the list
        newThumbnails.push({
          url,
          pageNumber,
          score
        })
      }

      // We sort the thumbnails by score and return them
      if (viewerState.isOnlyShowAttributeSourcePages.value) {
        thumbnails.value = newThumbnails.sort((a, b) => {
          return b.score - a.score
        })
      } else {
        thumbnails.value = newThumbnails
      }
    },
    { immediate: true }
  )

  return {
    thumbnails,
    gotoPage
  }
}

export const useSetupPdfViewer = props => {
  const viewerState = setupViewerState(props)
  provide("VIEWER_STATE", viewerState)

  const pdfDocument = shallowRef(undefined)
  provide("PDF_DOCUMENT", pdfDocument)

  const keyboardState = setupKeyboard()
  provide("KEYBOARD_STATE", keyboardState)

  const containerState = setupContainer()
  provide("CONTAINER_STATE", containerState)

  const pagingState = setupPaging({
    viewerState,
    pdfDocument,
    containerState
  })
  provide("PAGING_STATE", pagingState)

  const currentPageState = setupCurrentPage({
    viewerState,
    pdfDocument,
    pagingState
  })
  provide("CURRENT_PAGE_STATE", currentPageState)

  const scalingState = setupScaling({
    isShowExtractedWords: viewerState.isShowExtractedWords,
    isForceShowHighlightBlocks: viewerState.isForceShowHighlightBlocks,
    seeSourceAttributeSlug: viewerState.seeSourceAttributeSlug,
    initialScale: viewerState.initialScale,
    containerState,
    currentPageState
  })
  provide("SCALING_STATE", scalingState)

  setupPanning({ keyboardState, containerState })

  const controlState = setupControl({
    viewerState,
    pagingState,
    scalingState
  })
  provide("CONTROL_STATE", controlState)

  const wordTooltipState = setupWordTooltip({
    isShowExtractedWords: viewerState.isShowExtractedWords,
    isForceShowHighlightBlocks: viewerState.isForceShowHighlightBlocks,
    seeSourceAttributeSlug: viewerState.seeSourceAttributeSlug,
    containerState,
    currentPageState,
    scalingState
  })
  provide("WORD_TOOLTIP_STATE", wordTooltipState)

  const thumbnailsState = setupThumbnails({
    viewerState,
    pdfDocument,
    containerState,
    pagingState
  })
  provide("THUMBNAILS_STATE", thumbnailsState)

  const loadingState = setupLoading({
    containerState,
    currentPageState
  })
  provide("LOADING_STATE", loadingState)

  watch(
    viewerState.pdfUrl,
    async pdfUrl => {
      pdfDocument.value = undefined

      if (pdfUrl) {
        pdfDocument.value = await pdfjsLib.getDocument({
          url: pdfUrl,
          cMapUrl: "/pdf_cmaps/",
          cMapPacked: true,
          enableXfa: true
        }).promise
      }
    },
    { immediate: true }
  )

  /// Dynamically change detail page urls to pass states
  watch(
    [
      viewerState.isShowExtractedWords,
      viewerState.isForceShowHighlightBlocks,
      pagingState.currentPage,
      currentPageState.containerCenterRatio,
      scalingState.scale,
      scalingState.fitMode,
      scalingState.isFitting
    ],
    ([
      isShowExtractedWords,
      isForceShowHighlightBlocks,
      pagingCurrentPage,
      currentPageContainerCenterRatio,
      scalingScale,
      scalingFitMode,
      scalingIsFitting
    ]) => {
      if (
        typeof pagingCurrentPage === "undefined" ||
        typeof scalingScale === "undefined" ||
        typeof scalingFitMode === "undefined" ||
        typeof scalingIsFitting === "undefined"
      ) {
        return
      }

      if (typeof viewerState.detailPageUrl.value !== "undefined") {
        const url = new URL(viewerState.detailPageUrl.value, window.location.origin)

        if (isShowExtractedWords) {
          url.searchParams.set("is_show_extracted_words", "true")
        } else {
          url.searchParams.delete("is_show_extracted_words")
        }

        if (isForceShowHighlightBlocks) {
          url.searchParams.set("is_force_show_highlight_blocks", "true")
        } else {
          url.searchParams.delete("is_force_show_highlight_blocks")
        }

        if (pagingCurrentPage) {
          url.searchParams.set("current_pdf_page", pagingCurrentPage.toString())

          if (currentPageContainerCenterRatio && currentPageContainerCenterRatio.pageNumber === pagingCurrentPage) {
            url.searchParams.set("center_ratio_top", currentPageContainerCenterRatio.top.toString())
            url.searchParams.set("center_ratio_left", currentPageContainerCenterRatio.left.toString())
          }
        }

        if (scalingFitMode !== "width" || !scalingIsFitting) {
          url.searchParams.set("scale", scalingScale.toString())
        } else {
          url.searchParams.delete("scale")
        }

        viewerState.detailPageUrl.value = url.toString()
      }
    },
    { immediate: true }
  )

  return {
    viewerState,
    pdfDocument,
    containerState,
    pagingState,
    currentPageState,
    scalingState,
    controlState,
    wordTooltipState,
    thumbnailsState,
    loadingState
  }
}
